import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { Check, ChevronDown, Languages } from 'lucide-react';
import classNames from 'classnames';
import { AvailableLanguageInterface } from '../../services/exports/Interfaces';
import useLanguage from '../../hooks/localization/useLanguage';

interface Props {
  className?: string;
}

/**
 * The customer-facing language picker. Renders nothing while the restaurant has only
 * one active language, so a single-language storefront is unchanged.
 *
 * A custom trigger + panel rather than a native `<select>`: the native control renders
 * as the OS widget, which ignores the storefront's brand tokens and looked foreign next
 * to the rest of the header. This mirrors the dashboard's picker — a bordered trigger
 * showing the current language, and a floating list with the active one checked.
 */
const LanguageSelector = ({ className }: Props): ReactElement | null => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Headers:PageHeader' });

  const { language, languages, changeLanguage, hasChoice } = useLanguage();

  const [open, setOpen] = useState<boolean>(false);

  if (!hasChoice) {
    return null;
  }

  const current = languages.find((item: AvailableLanguageInterface) => item.language === language);

  async function select(next: string): Promise<void> {
    setOpen(false);
    await changeLanguage(next);
  }

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div className={classNames('relative', className)}>
        <button
          type="button"
          onClick={() => setOpen((isOpen) => !isOpen)}
          onKeyDown={(event) => event.key === 'Escape' && setOpen(false)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={t('language_selector.label')}
          className={trigger}
        >
          <Languages className="w-4 h-4 shrink-0" aria-hidden="true" />
          {/* The endonym is the useful label, but it costs width the mobile header does
              not have beside the profile and basket buttons — so drop to the code. */}
          <span className="hidden sm:inline truncate">{current?.languageName ?? language}</span>
          <span className="sm:hidden uppercase">{language}</span>
          <ChevronDown
            className={classNames('w-3.5 h-3.5 shrink-0 transition-transform', { 'rotate-180': open })}
            aria-hidden="true"
          />
        </button>

        {open && (
          <ul role="listbox" aria-label={t('language_selector.label')} className={panel}>
            {languages.map((item: AvailableLanguageInterface) => {
              const active = item.language === language;

              return (
                <li key={item.language}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => select(item.language)}
                    className={classNames(option, {
                      '!text-brand-primary font-medium': active,
                    })}
                  >
                    <span className="truncate">{item.languageName}</span>
                    {active && <Check className="w-4 h-4 shrink-0" aria-hidden="true" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </ClickAwayListener>
  );
};

export default LanguageSelector;

const trigger =
  'flex items-center gap-x-2xs rounded-md border border-brand-border bg-brand-background-primary ' +
  'px-xs py-2xs text-xs font-medium text-muted-foreground transition-colors ' +
  'hover:bg-brand-background-hover hover:text-brand-text-primary';

// `shadow` (not shadow-lg): the theme replaces Tailwind's boxShadow scale wholesale
// with DEFAULT/icon/section/none, so the numbered variants are dead classes here.
const panel =
  'absolute right-0 top-[calc(100%+4px)] z-50 min-w-[10rem] list-none overflow-hidden ' +
  'rounded-md border border-brand-border bg-brand-background-primary p-none shadow dark:shadow-none';

const option =
  'flex w-full items-center justify-between gap-x-sm px-sm py-xs text-left text-xs ' +
  'text-brand-text-primary transition-colors hover:bg-brand-background-hover';
